import React, { useState } from 'react'
import { Layers, ChevronDown, ChevronRight } from 'lucide-react'
import { formatCompact, formatPercent, formatPrice, formatSignedPercent } from '../utils/format'
import { toneColor } from '../utils/tone'

const returnTone = (value) => ((value ?? 0) >= 0 ? 'bull' : 'bear')

const formatCap = (value) => {
  const abs = Math.abs(value ?? 0)
  if (abs >= 1e9) return `$${((value ?? 0) / 1e9).toFixed(2)}B`
  if (abs >= 1e6) return `$${((value ?? 0) / 1e6).toFixed(0)}M`
  return formatPrice(value)
}

export default function DilutionPanel({ dilution }) {
  const [detailsOpen, setDetailsOpen] = useState(false)
  if (!dilution?.currentShares) return null

  const forward = dilution.forward
  const analyst = dilution.analystCheck
  const history = dilution.history ?? []

  return (
    <div className="onds-card p-4 md:p-5">
      <div className="flex items-center justify-between gap-3 mb-1 flex-wrap">
        <div className="flex items-center gap-2">
          <Layers className="h-5 w-5" style={{ color: 'var(--onds-accent)' }} />
          <h2 className="text-base md:text-lg font-bold">Dilution Lens</h2>
          <span className="text-sm text-[var(--onds-text-dim)]">the price chart lies</span>
        </div>
        <span className="onds-chip text-[11px] tabular-nums">
          {formatCompact(dilution.currentShares)} shares · {formatCap(dilution.currentMarketCap)}
        </span>
      </div>

      <p className="text-xs text-[var(--onds-text-dim)] mb-4">
        The share count has grown so fast that old prices are not comparable. The price is{' '}
        <span className="font-semibold" style={{ color: toneColor(returnTone(dilution.priceVsPeakRatio)) }}>
          {formatSignedPercent(dilution.priceVsPeakRatio)}
        </span>
        {' '}from its peak, but the company&apos;s valuation is only{' '}
        <span className="font-semibold" style={{ color: toneColor(returnTone(dilution.marketCapVsPeakRatio)) }}>
          {formatSignedPercent(dilution.marketCapVsPeakRatio)}
        </span>
        {' '}from its peak.
      </p>

      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-3">
        <Card
          label="Dilution-adjusted peak"
          value={formatPrice(dilution.dilutionAdjustedPeakPrice)}
          hint={`not ${formatPrice(dilution.peakPrice)} — same valuation, more shares`}
          highlight
        />
        <Card
          label={`Old ${formatPrice(dilution.peakPrice)} high would need`}
          value={formatCap(dilution.marketCapToRegainPeakPrice)}
          hint={`${(dilution.peakPriceMarketCapMultiple ?? 0).toFixed(2)}x the highest valuation ever carried`}
          tone="bear"
        />
        {forward && (
          <Card
            label="Forward dilution · 1 year"
            value={`${formatSignedPercent(forward.projectedDilutionAtLatestPace)} to ${formatSignedPercent(forward.projectedDilutionOneYear)}`}
            hint={`market cap must grow this much just to hold price flat`}
            tone="bear"
          />
        )}
        {forward?.quartersOfRunway > 0 && (
          <Card
            label="Cash runway"
            value={`${forward.quartersOfRunway.toFixed(1)} quarters`}
            hint="at the latest quarter's burn — a dated raise risk"
          />
        )}
      </div>

      {analyst && (
        <div className="mt-3 rounded-lg p-3 border" style={{ backgroundColor: 'var(--onds-surface)', borderColor: 'var(--onds-border)' }}>
          <p className="text-[11px] font-bold mb-1">Do analyst targets account for dilution?</p>
          <p className="text-xs text-[var(--onds-text-dim)] leading-relaxed">
            The {formatPrice(analyst.targetMean)} mean target implies{' '}
            <span className="font-semibold" style={{ color: 'var(--onds-text)' }}>{formatCap(analyst.targetImpliedMarketCap)}</span>
            {' '}of market cap — {(analyst.targetMultipleOfPeakMarketCap ?? 0).toFixed(2)}x the all-time peak valuation. Against the
            projected share count a year out, that same valuation is worth only{' '}
            <span className="font-semibold" style={{ color: toneColor('bear') }}>{formatPrice(analyst.targetAdjustedForProjectedDilution)}</span>
            {' '}per share.
          </p>
        </div>
      )}

      {history.length > 0 && (
        <>
          <button
            onClick={() => setDetailsOpen((open) => !open)}
            className="flex items-center gap-1.5 mt-4 text-xs font-semibold"
            style={{ color: 'var(--onds-text-dim)' }}
          >
            {detailsOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            {detailsOpen ? 'Hide' : 'Show'} share count &amp; valuation history
          </button>

          {detailsOpen && <HistoryTable history={history} issuance={dilution.issuance} />}
        </>
      )}
    </div>
  )
}

function Card({ label, value, hint, tone, highlight }) {
  return (
    <div
      className="rounded-lg p-3 border"
      style={{
        backgroundColor: 'var(--onds-surface)',
        borderColor: highlight ? 'var(--onds-accent)' : 'var(--onds-border)',
      }}
    >
      <p className="text-[10px] text-[var(--onds-text-dim)] mb-1 leading-tight">{label}</p>
      <p className="text-sm font-bold tabular-nums" style={tone ? { color: toneColor(tone) } : undefined}>{value}</p>
      {hint && <p className="text-[10px] text-[var(--onds-text-dim)] mt-0.5 leading-tight">{hint}</p>}
    </div>
  )
}

function HistoryTable({ history, issuance }) {
  return (
    <div className="mt-3">
      {issuance && (
        <p className="text-[11px] text-[var(--onds-text-dim)] mb-2">
          Recent pace {formatPercent(issuance.recentPercentPerQuarter, 1)} per quarter · latest quarter{' '}
          {formatPercent(issuance.latestPercentPerQuarter, 1)} · {formatCompact(issuance.sharesAddedLastYear)} shares added over the last year
        </p>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-[10px] text-[var(--onds-text-dim)]">
              <th className="text-left font-semibold py-1 pr-3">Quarter</th>
              <th className="text-right font-semibold py-1 pr-3">Price</th>
              <th className="text-right font-semibold py-1 pr-3">Shares</th>
              <th className="text-right font-semibold py-1">Market cap</th>
            </tr>
          </thead>
          <tbody>
            {history.map((point) => (
              <tr key={point.date} className="border-t" style={{ borderColor: 'var(--onds-border)' }}>
                <td className="py-1 pr-3 font-semibold whitespace-nowrap">{String(point.date).slice(0, 7)}</td>
                <td className="py-1 pr-3 text-right tabular-nums">{formatPrice(point.price)}</td>
                <td className="py-1 pr-3 text-right tabular-nums">{formatCompact(point.shares)}</td>
                <td className="py-1 text-right tabular-nums font-semibold">{formatCap(point.marketCap)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[10px] text-[var(--onds-text-dim)] mt-2">
        Read the market cap column, not the price column, to judge whether the market is re-rating the business.
      </p>
    </div>
  )
}
